/**
 * useAdminAuditMutation
 *
 * Wraps {@link useMutation} (TanStack Query) with the cross-cutting
 * concerns every Wave 1b admin dialog needs:
 *
 *   1. capture a free-text reason for the audit trail (>= 3 chars)
 *   2. require a typed-confirm phrase before the request fires
 *   3. require a second-approver name (2-person rule)
 *   4. emit a categorised audit log entry on success / failure
 *   5. invalidate one or more TanStack Query keys on success
 *   6. surface a toast and reset internal state when finished
 *
 * The state machine is exposed so dialog components can drive their UI
 * from a single source of truth instead of juggling six `useState`
 * hooks per dialog (the previous pattern across ~35 admin dialogs).
 *
 * State machine (deterministic, single-process):
 *
 *   idle
 *    └── submit() ──┬─ requireReason && !reason   ─→ awaiting_reason
 *                   ├─ requireConfirm && !typed   ─→ awaiting_typed_confirm
 *                   ├─ require2Person && !appr.   ─→ awaiting_second_approver
 *                   └─ all gates green            ─→ submitting
 *                                                    ├─ success ──→ success
 *                                                    └─ error   ──→ error
 *   error/success ── reset() ──→ idle
 *
 * NOTE: this hook does NOT call the audit-log API directly — server-side
 * route handlers already write to the hash-chained audit table from the
 * request context (`@/services/audit.service.ts`). What this hook does
 * is forward the captured `reason` / `secondApprover` fields to the
 * mutation as `auditMeta` so the route handler can attach them.
 *
 * Wave 1b W1b-37 — admin dialog deduplication.
 */

import { useCallback, useMemo, useState } from 'react';
import { useMutation, useQueryClient, type QueryKey } from '@tanstack/react-query';
import { toast } from 'sonner';

// ─── public types ─────────────────────────────────────────────────────────

/**
 * Mirrors the server-side AuditCategory enum from
 * `packages/api/src/services/audit.service.ts`.  Kept inline here so
 * the web package doesn't import from `@argusai/api` (which would create
 * a server-only dependency in the browser bundle).
 */
export type AuditCategory =
  | 'authentication'
  | 'authorization'
  | 'user_management'
  | 'organization_management'
  | 'data_access'
  | 'data_modification'
  | 'system';

export type AdminAuditMutationState =
  | 'idle'
  | 'awaiting_reason'
  | 'awaiting_typed_confirm'
  | 'awaiting_second_approver'
  | 'submitting'
  | 'success'
  | 'error';

export interface AuditMeta {
  reason?: string;
  secondApprover?: string;
}

export interface UseAdminAuditMutationOptions<TVars, TResult> {
  /**
   * The mutation function.  Receives both the caller's variables and
   * the captured audit metadata (reason / second approver) so the
   * dialog only needs to concern itself with the domain payload.
   */
  mutationFn: (vars: TVars, audit: AuditMeta) => Promise<TResult>;

  /** Audit category for this action (e.g. "data_modification"). */
  category: AuditCategory;

  /** Action name in dot-notation (e.g. "tenant.slug_renamed"). */
  action: string;

  /**
   * When true, requires a non-empty reason of >= 3 chars before
   * submitting.  The reason is forwarded as `audit.reason`.
   */
  requireReason?: boolean;

  /**
   * When true, requires the user to type the configured confirm phrase
   * exactly before submitting.  The phrase itself comes from
   * `phraseToConfirm`.
   */
  requireTypedConfirm?: { phrase: string };

  /**
   * When true, requires a non-empty second-approver name (>= 2 chars)
   * before submitting.  Forwarded as `audit.secondApprover`.
   */
  require2Person?: boolean;

  /** TanStack Query keys to invalidate on success. */
  invalidateKeys?: QueryKey[];

  /** Toast message on success.  Falls back to `${action} succeeded`. */
  successMessage?: string;

  /**
   * Toast message on error.  Receives the thrown error so callers can
   * inspect it; defaults to `err.message`.
   */
  errorMessage?: (err: Error) => string;

  /** Called on successful resolution — useful for closing dialogs. */
  onSuccess?: (result: TResult) => void;
}

export interface UseAdminAuditMutationReturn<TVars, TResult> {
  /**
   * Attempt to submit.  Walks the gate state machine; the `state`
   * field tells the dialog what it should ask the user for next.
   * Returns the result on success, or rejects on error.
   */
  submit: (vars: TVars) => Promise<TResult | undefined>;

  /** Current FSM state. */
  state: AdminAuditMutationState;

  /** Reason field bound to the dialog textarea. */
  reason: string;
  setReason: (s: string) => void;

  /** Typed-confirm input bound to the dialog input. */
  typedConfirmInput: string;
  setTypedConfirmInput: (s: string) => void;

  /** Second-approver input bound to the dialog input. */
  secondApproverInput: string;
  setSecondApproverInput: (s: string) => void;

  /** Reset all state back to idle (e.g. on dialog close). */
  reset: () => void;

  /** Convenience: state === 'submitting'. */
  isLoading: boolean;

  /** The error from the last attempt, if any. */
  error: Error | null;

  /** The result from the last successful attempt, if any. */
  data: TResult | null;
}

// ─── pure helper: gate evaluation ─────────────────────────────────────────

export interface GateInputs {
  reason: string;
  typedConfirmInput: string;
  secondApproverInput: string;
}

export interface GateConfig {
  requireReason: boolean;
  requireTypedConfirm: { phrase: string } | undefined;
  require2Person: boolean;
}

/**
 * Evaluates the FSM gates against the current input state and returns
 * the next state.  Pure function, exported for unit testing — the React
 * hook below is a thin wrapper around it.
 *
 * Returns:
 *   - `'awaiting_reason'`            -- requireReason set but reason < 3 chars
 *   - `'awaiting_typed_confirm'`     -- typed-confirm gate set but no match
 *   - `'awaiting_second_approver'`   -- require2Person set but approver < 2 chars
 *   - `'submitting'`                 -- all gates green, ready to submit
 */
export function evaluateGates(
  inputs: GateInputs,
  config: GateConfig
): 'awaiting_reason' | 'awaiting_typed_confirm' | 'awaiting_second_approver' | 'submitting' {
  if (config.requireReason && inputs.reason.trim().length < 3) {
    return 'awaiting_reason';
  }
  if (
    config.requireTypedConfirm &&
    inputs.typedConfirmInput !== config.requireTypedConfirm.phrase
  ) {
    return 'awaiting_typed_confirm';
  }
  if (config.require2Person && inputs.secondApproverInput.trim().length < 2) {
    return 'awaiting_second_approver';
  }
  return 'submitting';
}

/**
 * Builds the {@link AuditMeta} payload from current input state, only
 * including fields whose corresponding gate is enabled.  Pure function;
 * exported for unit testing.
 */
export function buildAuditMeta(
  inputs: GateInputs,
  config: Pick<GateConfig, 'requireReason' | 'require2Person'>
): AuditMeta {
  const audit: AuditMeta = {};
  if (config.requireReason) audit.reason = inputs.reason.trim();
  if (config.require2Person) audit.secondApprover = inputs.secondApproverInput.trim();
  return audit;
}

// ─── implementation ───────────────────────────────────────────────────────

export function useAdminAuditMutation<TVars, TResult>(
  opts: UseAdminAuditMutationOptions<TVars, TResult>
): UseAdminAuditMutationReturn<TVars, TResult> {
  const queryClient = useQueryClient();

  const [reason, setReason] = useState('');
  const [typedConfirmInput, setTypedConfirmInput] = useState('');
  const [secondApproverInput, setSecondApproverInput] = useState('');
  const [state, setState] = useState<AdminAuditMutationState>('idle');
  const [error, setError] = useState<Error | null>(null);
  const [data, setData] = useState<TResult | null>(null);

  const mutation = useMutation<TResult, Error, { vars: TVars; audit: AuditMeta }>({
    mutationFn: ({ vars, audit }) => opts.mutationFn(vars, audit),
    onSuccess: (result) => {
      setData(result);
      setState('success');
      // Invalidate every requested cache key.
      for (const key of opts.invalidateKeys ?? []) {
        queryClient.invalidateQueries({ queryKey: key });
      }
      toast.success(opts.successMessage ?? `${opts.action} succeeded`);
      opts.onSuccess?.(result);
    },
    onError: (err) => {
      setError(err);
      setState('error');
      const fmt = opts.errorMessage ?? ((e: Error) => e.message || 'Action failed');
      toast.error(fmt(err));
    },
  });

  const reset = useCallback(() => {
    setReason('');
    setTypedConfirmInput('');
    setSecondApproverInput('');
    setState('idle');
    setError(null);
    setData(null);
    mutation.reset();
  }, [mutation]);

  const submit = useCallback(
    async (vars: TVars): Promise<TResult | undefined> => {
      const gateConfig: GateConfig = {
        requireReason: opts.requireReason ?? false,
        requireTypedConfirm: opts.requireTypedConfirm,
        require2Person: opts.require2Person ?? false,
      };
      const gateInputs: GateInputs = { reason, typedConfirmInput, secondApproverInput };

      const next = evaluateGates(gateInputs, gateConfig);
      if (next !== 'submitting') {
        setState(next);
        return undefined;
      }

      // ── all gates green: submit ───
      setState('submitting');
      setError(null);

      const audit = buildAuditMeta(gateInputs, {
        requireReason: gateConfig.requireReason,
        require2Person: gateConfig.require2Person,
      });

      try {
        const result = await mutation.mutateAsync({ vars, audit });
        return result;
      } catch (err) {
        // mutation.onError already set state/error/toasted; rethrow
        // so the caller can `await submit().catch(...)` if it wants.
        throw err instanceof Error ? err : new Error(String(err));
      }
    },
    [
      opts.requireReason,
      opts.requireTypedConfirm,
      opts.require2Person,
      reason,
      typedConfirmInput,
      secondApproverInput,
      mutation,
    ]
  );

  return useMemo(
    () => ({
      submit,
      state,
      reason,
      setReason,
      typedConfirmInput,
      setTypedConfirmInput,
      secondApproverInput,
      setSecondApproverInput,
      reset,
      isLoading: state === 'submitting',
      error,
      data,
    }),
    [
      submit,
      state,
      reason,
      typedConfirmInput,
      secondApproverInput,
      reset,
      error,
      data,
    ]
  );
}
